import React from 'react';
import { 
  Box, 
  Skeleton, 
  Card, 
  CardContent, 
  Grid, 
  useMediaQuery,
  useTheme 
} from '@mui/material';

// Generic skeleton for cards/widgets
export const CardSkeleton = ({ height = 120, showAvatar = false }) => (
  <Card sx={{ mb: 2 }}>
    <CardContent>
      <Box display="flex" alignItems="center" mb={1}>
        {showAvatar && (
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
        )}
        <Box flexGrow={1}>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="40%" height={20} />
        </Box>
      </Box>
      <Skeleton variant="rectangular" height={height} sx={{ borderRadius: 1 }} />
    </CardContent>
  </Card>
);

// Skeleton for deck cards
export const DeckCardSkeleton = () => (
  <Card sx={{ height: '100%', position: 'relative' }}>
    <CardContent>
      <Box display="flex" alignItems="center" mb={2}>
        <Skeleton variant="circular" width={32} height={32} sx={{ mr: 1 }} />
        <Skeleton variant="text" width="70%" height={24} />
      </Box>
      <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
      <Skeleton variant="text" width="80%" height={20} sx={{ mb: 2 }} />
      
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 12 }} />
        <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 12 }} />
      </Box>
      
      <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
    </CardContent>
  </Card>
);

// Skeleton for user profile widget
export const UserWidgetSkeleton = () => (
  <Card>
    <CardContent>
      <Box display="flex" alignItems="center" mb={2}>
        <Skeleton variant="circular" width={56} height={56} sx={{ mr: 2 }} />
        <Box flexGrow={1}>
          <Skeleton variant="text" width="70%" height={24} />
          <Skeleton variant="text" width="50%" height={20} />
        </Box>
      </Box>
      <Box display="flex" justifyContent="space-around" mt={2}>
        <Box textAlign="center">
          <Skeleton variant="text" width={40} height={32} />
          <Skeleton variant="text" width={50} height={16} />
        </Box>
        <Box textAlign="center">
          <Skeleton variant="text" width={40} height={32} />
          <Skeleton variant="text" width={50} height={16} />
        </Box>
        <Box textAlign="center">
          <Skeleton variant="text" width={40} height={32} />
          <Skeleton variant="text" width={50} height={16} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// Skeleton for stats dashboard
export const StatsDashboardSkeleton = () => {
  const theme = useTheme();
  const isNonMobileScreens = useMediaQuery("(min-width:1000px)");
  
  return (
    <Grid container spacing={2}>
      {/* Level and Experience Card */}
      <Grid item xs={12} sm={6} md={4}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
              <Box flexGrow={1}>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="40%" height={20} />
              </Box>
            </Box>
            <Skeleton variant="rectangular" height={8} sx={{ borderRadius: 4, mb: 1 }} />
            <Skeleton variant="text" width="30%" height={16} />
          </CardContent>
        </Card>
      </Grid>

      {/* Today's Progress */}
      <Grid item xs={12} sm={6} md={4}>
        <Card>
          <CardContent>
            <Skeleton variant="text" width="70%" height={24} sx={{ mb: 2 }} />
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Skeleton variant="text" width="40%" height={20} />
              <Skeleton variant="text" width="20%" height={20} />
            </Box>
            <Skeleton variant="rectangular" height={8} sx={{ borderRadius: 4, mb: 2 }} />
            <Box display="flex" justifyContent="space-between">
              <Skeleton variant="text" width="30%" height={16} />
              <Skeleton variant="text" width="30%" height={16} />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Streak Card */}
      <Grid item xs={12} sm={6} md={4}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Skeleton variant="circular" width={32} height={32} sx={{ mr: 2 }} />
              <Skeleton variant="text" width="60%" height={24} />
            </Box>
            <Box textAlign="center">
              <Skeleton variant="text" width="40%" height={48} sx={{ mx: 'auto', mb: 1 }} />
              <Skeleton variant="text" width="60%" height={20} sx={{ mx: 'auto' }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Study Calendar */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Skeleton variant="text" width="30%" height={24} sx={{ mb: 2 }} />
            <Grid container spacing={1}>
              {Array.from({ length: 35 }).map((_, index) => (
                <Grid item key={index}>
                  <Skeleton variant="rectangular" width={20} height={20} sx={{ borderRadius: 1 }} />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Quick Actions */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Skeleton variant="text" width="40%" height={24} sx={{ mb: 2 }} />
            <Box display="flex" gap={2} flexWrap="wrap">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} variant="rectangular" width={120} height={40} sx={{ borderRadius: 1 }} />
              ))}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// Skeleton for decks page
export const DecksPageSkeleton = () => {
  const isNonMobileScreens = useMediaQuery("(min-width:1000px)");
  
  return (
    <Box>
      {/* Header with filters */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Skeleton variant="text" width="200px" height={32} />
        <Box display="flex" gap={2}>
          <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 1 }} />
        </Box>
      </Box>

      {/* Deck Grid */}
      <Grid container spacing={3}>
        {Array.from({ length: isNonMobileScreens ? 8 : 4 }).map((_, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            <DeckCardSkeleton />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// Generic page skeleton with navbar and sidebar
export const PageSkeleton = ({ children, showSidebar = true }) => {
  const isNonMobileScreens = useMediaQuery("(min-width:1000px)");
  
  return (
    <Box>
      {/* Navbar skeleton */}
      <Box 
        sx={{ 
          height: 60, 
          borderBottom: 1, 
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          px: 2,
          mb: 2
        }}
      >
        <Skeleton variant="text" width="120px" height={32} />
        <Box sx={{ flexGrow: 1 }} />
        <Box display="flex" gap={2} alignItems="center">
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="circular" width={40} height={40} />
        </Box>
      </Box>

      <Box
        width="100%"
        padding="2rem 6%"
        display={isNonMobileScreens ? "flex" : "block"}
        gap="0.5rem"
        justifyContent="space-between"
      >
        {/* Sidebar skeleton */}
        {showSidebar && isNonMobileScreens && (
          <Box flexBasis="26%">
            <UserWidgetSkeleton />
          </Box>
        )}

        {/* Main content */}
        <Box 
          flexBasis={isNonMobileScreens && showSidebar ? "42%" : "100%"}
          mt={isNonMobileScreens ? undefined : "2rem"}
        >
          {children}
        </Box>

        {/* Right sidebar skeleton */}
        {showSidebar && isNonMobileScreens && (
          <Box flexBasis="26%">
            <Card>
              <CardContent>
                <Skeleton variant="text" width="60%" height={24} sx={{ mb: 2 }} />
                {Array.from({ length: 3 }).map((_, index) => (
                  <Box key={index} display="flex" alignItems="center" mb={2}>
                    <Skeleton variant="circular" width={32} height={32} sx={{ mr: 2 }} />
                    <Box flexGrow={1}>
                      <Skeleton variant="text" width="70%" height={20} />
                      <Skeleton variant="text" width="50%" height={16} />
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Default export with all skeletons
const SkeletonLoader = {
  Card: CardSkeleton,
  DeckCard: DeckCardSkeleton,
  UserWidget: UserWidgetSkeleton,
  StatsDashboard: StatsDashboardSkeleton,
  DecksPage: DecksPageSkeleton,
  Page: PageSkeleton,
};

export default SkeletonLoader;
